import { CampaignChannels, ABTestType } from 'domains/campaigns/types';
import { isTemplate } from 'domains/content/types';
import useSenderProfile from 'hooks/queries/useSenderProfile';
import useTemplate from 'hooks/queries/useTemplate';
import { CampaignStateTypes } from '../customCampaignReducer';
import VoicePreviewContent from 'domains/content/components/VoicePreviewContent';
import SmsPreview from 'pages/ContentSmsBuilderPage/components/SmsPreview';
import useFormatSmsContent from 'pages/ContentSmsBuilderPage/hooks/useFormatSmsContent';
import useSmsData from 'pages/ContentSmsBuilderPage/hooks/useSmsData';
import useDirectMail from 'hooks/queries/useDirectMail';
import EmailPreviewContent from 'domains/content/components/EmailPreviewContent/EmailPreviewContent';
import DirectMailPreviewContent from 'domains/content/components/DirectMailPreviewContent/DirectMailPreviewContent';
import useDirectMailSenderProfile from 'hooks/queries/useDirectMailSenderProfile';
import useVoiceContentById from 'hooks/queries/useVoiceContentById';
import { getFormattedSmsAutoResponses } from 'pages/ContentSmsBuilderPage/components/SmsPreview/utils';
import ABTestEmailPreview from './ABTestEmailPreview';

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    contentPreviewId?: number;
    setContentPreviewId: (id: number) => void;
};

const CustomCampaignContentPreview = ({
    activeSendIndex,
    campaignState,
    contentPreviewId,
    setContentPreviewId,
}: Props) => {
    const currentSend = campaignState.sends[activeSendIndex];
    const isCurrentSendDirectMail = currentSend.channel === CampaignChannels.DIRECT_MAIL;
    const isCurrentSendEmail = currentSend.channel === CampaignChannels.EMAIL;
    const isCurrentSendSms = currentSend.channel === CampaignChannels.SMS;
    const isCurrentSendVoice = currentSend.channel === CampaignChannels.VOICE_CALL;

    const senderProfileId = isCurrentSendEmail || isCurrentSendDirectMail ? currentSend?.senderProfileId : null;

    const { data: previewDirectMail } = useDirectMail(contentPreviewId, {
        enabled: isCurrentSendDirectMail && !!contentPreviewId,
    });

    const { data: directMailSenderProfile } = useDirectMailSenderProfile({
        senderProfileId,
        reactQueryOptions: { enabled: isCurrentSendDirectMail && !!senderProfileId },
    });

    const { data: previewTemplate } = useTemplate({
        templateId: `${contentPreviewId}`,
        reactQueryOptions: {
            enabled: isCurrentSendEmail && !!contentPreviewId,
        },
    });

    const { data: emailSenderProfile } = useSenderProfile(senderProfileId, {
        enabled: isCurrentSendEmail && !!senderProfileId,
    });

    const { smsData, mergeTags } = useSmsData({
        id: `${contentPreviewId}`,
        enabled: isCurrentSendSms && !!contentPreviewId,
    });

    const formattedSmsPreviewContent = useFormatSmsContent(smsData?.content, mergeTags);
    const formattedSmsAutoResponses = getFormattedSmsAutoResponses(smsData?.autoResponses, mergeTags);

    const { data: voiceData } = useVoiceContentById({
        voiceContentId: `${contentPreviewId}`,
        reactQueryOptions: {
            enabled: isCurrentSendVoice && !!contentPreviewId,
        },
    });

    if (isCurrentSendDirectMail) {
        return (
            <DirectMailPreviewContent
                isOpen={!!contentPreviewId}
                htmlContent={previewDirectMail?.html}
                isGridItem
                onClose={() => setContentPreviewId(null)}
                directMailSender={directMailSenderProfile}
            />
        );
    }

    if (isCurrentSendEmail) {
        // Check if this is an A/B content test campaign
        // Only show A/B preview for content testing (not send time testing)
        // because content testing has two different emails in sends[0] and sends[1]
        const isABContentTest = campaignState.abTestConfig?.testOn === ABTestType.CONTENT;

        // If A/B content test, use the ABTestEmailPreview component with tabs
        if (isABContentTest) {
            return <ABTestEmailPreview campaignState={campaignState} onClose={() => setContentPreviewId(null)} />;
        }

        // Otherwise, use the regular email preview (for normal emails or send time A/B tests)
        return (
            <EmailPreviewContent
                emailTemplateHTML={isTemplate(previewTemplate) && previewTemplate.html}
                fromAddress={emailSenderProfile?.from_address}
                isGridItem
                isOpen={!!contentPreviewId}
                preHeader={currentSend?.preHeader}
                subjectLine={currentSend?.subjectLine}
                onClose={() => setContentPreviewId(null)}
            />
        );
    }

    if (isCurrentSendSms) {
        return <SmsPreview formattedContent={formattedSmsPreviewContent} autoResponses={formattedSmsAutoResponses} />;
    }

    if (isCurrentSendVoice) {
        return (
            <VoicePreviewContent
                isOpen={!!contentPreviewId}
                onClose={() => setContentPreviewId(null)}
                voiceJson={voiceData?.voiceJson}
            />
        );
    }
};

export default CustomCampaignContentPreview;
